import { HeaderProps, SearchSuggestion } from '@liveauctioneers/hammer-ui-liveauctioneers/types';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';

import { useQueryParams } from '@/hooks/queryParams';
import { useSubmitRecentSearch } from '@/hooks/useSubmitRecentSearch';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { setIsZeroResults, updateSearchSubmittedFromPage } from '@/redux/modules/search/search.actions';
import { getSearchInputBuffer } from '@/redux/modules/search/search.selectors';
import { fetchSearchSuggestions } from '@/redux/modules/searchSuggestions/searchSuggestions.actions';
import {
    getIsSearchSuggestionsLoading,
    getSearchSuggestions,
} from '@/redux/modules/searchSuggestions/searchSuggestions.selectors';
import { getIsWhiteLabel, getWhiteLabelSellerId } from '@/redux/modules/whiteLabel';
import { TrackSearchCompletedSearchType, useTrackSearchCompletedAnalytics } from '@/utils/amplitude/analytics';
import { SearchCompletedTrigger } from '@/utils/amplitude/analytics/core.types';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';
import {
    DEFAULT_STATUS_FOR_ARCHIVED_SEARCH,
    DEFAULT_STATUS_FOR_ONLINE_SEARCH,
    getKeywordFromQueryParams,
    parseQueryParams,
} from '@/utils/queryParams';
import { safelyEscapeText } from '@/utils/sanitize';
import { parseSearchSeoPath } from '@/utils/search/searchSeoPath';
import {
    getIsCollectionsPage,
    getIsSEOSearchPage,
    getIsSEOSearchPageWithLocation,
    getSearchUrlWithParams,
    getSellerUrl,
} from '@/utils/urls';

const SUGGESTIONS_DEBOUNCE_MS = 250;
const MIN_SUGGESTION_QUERY_LENGTH = 3;

export const useSearchProps = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const sellerId = useAppSelector(getWhiteLabelSellerId);

    const relatedSuggestions = useAppSelector(getSearchSuggestions);
    const suggestions: SearchSuggestion[] = relatedSuggestions
        .map(({ suggestions }) => suggestions)
        .flat()
        .map((s) => {
            return {
                ...s,
                houseId: s?.houseId,
                label: safelyEscapeText(s?.label), // safelyEscape for if the house name contains double byte characters
            };
        });

    const [typingState, setTypingState] = useState<{
        isTyping: boolean;
        query: string;
    }>({ isTyping: false, query: '' });

    const getSuggestionsDebounced = useDebouncedCallback((query: string) => {
        setTypingState((prev) => ({ ...prev, isTyping: false }));
        dispatch(fetchSearchSuggestions({ term: query, useScoutCategories: false }));
    }, SUGGESTIONS_DEBOUNCE_MS);

    const getSuggestions = useCallback(
        (query: string) => {
            setTypingState({ isTyping: true, query });
            getSuggestionsDebounced(query);
        },
        [getSuggestionsDebounced]
    );

    const { pathname, search } = useLocation();
    const isOnPriceGuidePage = pathname.includes('/price-guide/');
    const { getQueryParams } = useQueryParams();
    const { buyNow, status } = getQueryParams();
    const archiveSearchStatus = status === 'archive' && !buyNow && !isOnPriceGuidePage;
    const pageSize = useAppSelector(getSearchPageSize);
    const bufferFromRedux = useAppSelector(getSearchInputBuffer);

    // On search/collections pages, derive keyword from URL so the input reflects the shareable URL (direct load, refresh, chip click)
    const keywordFromUrl = useMemo(() => {
        const isSearchOrCollectionsPage =
            pathname === '/search' || pathname.startsWith('/search/') || pathname.includes('/collections/');
        if (!isSearchOrCollectionsPage) {
            return undefined;
        }

        if (getIsSEOSearchPageWithLocation(pathname)) {
            const parsed = parseSearchSeoPath(pathname);
            return parsed?.kind === 'location' ? parsed.keyword : '';
        }
        if (getIsSEOSearchPage(pathname)) {
            const parsed = parseSearchSeoPath(pathname);
            return parsed?.kind === 'plain' ? parsed.keyword : '';
        }
        if (getIsCollectionsPage(pathname)) {
            const segments = pathname.split('/').filter(Boolean);
            return segments.pop() ?? '';
        }
        const queryParams = parseQueryParams(search);
        const keyword = getKeywordFromQueryParams(queryParams);
        return typeof keyword === 'string' ? keyword : String(keyword ?? '');
    }, [pathname, search]);

    const keyword = keywordFromUrl !== undefined ? keywordFromUrl : bufferFromRedux;

    const isLoadingSuggestions = useAppSelector(getIsSearchSuggestionsLoading);
    const hasNoSuggestionsForQuery =
        typingState.query.length >= MIN_SUGGESTION_QUERY_LENGTH &&
        !typingState.isTyping &&
        !isLoadingSuggestions &&
        !suggestions.length;

    const trackSearchCompletedAnalytics = useTrackSearchCompletedAnalytics();
    const analyticsPageName = getAmplitudePageNameFromPath(pathname);
    const submitRecentSearch = useSubmitRecentSearch();

    const searchPhrase = useCallback(
        ({ keyword, suggestionClicked }: { keyword: string; suggestionClicked: boolean }) => {
            const cleanedKeyword = keyword.trim();
            const searchStatus = archiveSearchStatus
                ? DEFAULT_STATUS_FOR_ARCHIVED_SEARCH
                : DEFAULT_STATUS_FOR_ONLINE_SEARCH;

            const params: {
                house_id?: string;
                keyword: string;
                status: typeof DEFAULT_STATUS_FOR_ARCHIVED_SEARCH | typeof DEFAULT_STATUS_FOR_ONLINE_SEARCH;
            } = {
                keyword: cleanedKeyword,
                status: searchStatus,
            };

            if (isWhiteLabel) {
                params.house_id = `${sellerId}`;
            }

            dispatch(updateSearchSubmittedFromPage(analyticsPageName));
            const searchUrl = getSearchUrlWithParams(params);

            navigate(searchUrl, { state: { suggestionClicked } });

            submitRecentSearch(cleanedKeyword);
        },
        [analyticsPageName, archiveSearchStatus, dispatch, isWhiteLabel, navigate, sellerId, submitRecentSearch]
    );

    const searchHouse = useCallback(
        (houseId: number, houseName: string) => {
            trackSearchCompletedAnalytics({
                featuredLotIds: '',
                // House jump from the search bar has no result list / dominant category.
                mainL1Category: null,
                mainL2Category: null,
                numberOfSearchResults: null,
                pageSize,
                searchQuery: houseName,
                searchSuggestionUsed: true,
                searchType: TrackSearchCompletedSearchType.Auctioneer,
                trigger: SearchCompletedTrigger.SearchBar,
            });

            navigate({
                pathname: getSellerUrl(houseId, houseName),
            });
        },
        [navigate, pageSize, trackSearchCompletedAnalytics]
    );

    const handleSearch: HeaderProps['search']['handleSearch'] = useCallback(
        ({ houseId, query, suggestionClicked }) => {
            dispatch(setIsZeroResults(false));

            const cleanedQuery = query.trim();

            if (query.length === 0) {
                return;
            } else if (houseId > 0) {
                searchHouse(houseId, cleanedQuery);
            } else {
                searchPhrase({ keyword: query, suggestionClicked });
            }
        },
        [dispatch, searchHouse, searchPhrase]
    );

    return {
        getSuggestions,
        handleSearch,
        hasNoSuggestionsForQuery,
        queryText: keyword,
        suggestions,
    };
};
